// src/context/SocketContext.tsx
import React, { createContext, useEffect, useState, ReactNode } from 'react';
import { Socket } from 'socket.io-client';
import { initializeSocket, disconnectSocket } from '../services/socket/socketClient';
import { useAuth } from '../hooks';
import { logger } from '../utils/logger';
import { listenForSubscriptionEvents } from '../services/socket/subscriptionSocketService';

interface SocketContextType {
  isConnected: boolean;
  socket: Socket | null;
  reconnect: () => void;
}

export const SocketContext = createContext<SocketContextType>({
  isConnected: false,
  socket: null,
  reconnect: () => {}
});

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { isAuthenticated } = useAuth();
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  // Connect socket when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      try {
        const socketInstance = initializeSocket();
        
        setSocket(socketInstance);
        
        const handleConnect = () => {
          setIsConnected(true);
          setReconnectAttempts(0);
          logger.info('Socket connection established');
        };
        
        const handleDisconnect = () => {
          setIsConnected(false);
          logger.warn('Socket disconnected');
        };
        
        const handleConnectError = (error: Error) => {
          logger.error(`Socket connect error: ${error.message}`);
          // Increment reconnect attempts
          setReconnectAttempts(prev => prev + 1);
        };
        
        if (socketInstance) {
          socketInstance.on('connect', handleConnect);
          socketInstance.on('disconnect', handleDisconnect);
          socketInstance.on('connect_error', handleConnectError);
          
          // Set initial connected state
          setIsConnected(socketInstance.connected);
          
          // Subscribe to subscription events
          const unsubscribeSubscription = listenForSubscriptionEvents();
          
          return () => {
            if (socketInstance) {
              socketInstance.off('connect', handleConnect);
              socketInstance.off('disconnect', handleDisconnect);
              socketInstance.off('connect_error', handleConnectError);
            }
            
            // Unsubscribe from subscription events
            unsubscribeSubscription();
          };
        }
      } catch (error: any) {
        logger.error('Socket initialization error:', error.message);
      }
    } else {
      disconnectSocket();
      setSocket(null);
      setIsConnected(false);
    }
  }, [isAuthenticated]);
  
  // Add exponential backoff for reconnect attempts
  useEffect(() => {
    if (!isAuthenticated || isConnected || reconnectAttempts === 0) return;
    
    // Calculate delay with exponential backoff (max 30 seconds)
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts - 1), 30000);
    
    logger.info(`Socket reconnecting in ${delay}ms (attempt ${reconnectAttempts})`);
    
    const timeoutId = setTimeout(() => {
      if (!isConnected && isAuthenticated) {
        logger.info(`Attempting socket reconnection...`);
        disconnectSocket();
        const socketInstance = initializeSocket();
        setSocket(socketInstance);
      }
    }, delay);
    
    return () => clearTimeout(timeoutId);
  }, [reconnectAttempts, isConnected, isAuthenticated]);
  
  // Provide a manual reconnect function
  const reconnect = () => {
    if (!isAuthenticated) return;
    
    logger.info('Manual socket reconnection initiated');
    disconnectSocket();
    const socketInstance = initializeSocket();
    setSocket(socketInstance);
  };

  return (
    <SocketContext.Provider value={{ isConnected, socket, reconnect }}>
      {children}
    </SocketContext.Provider>
  );
};
